import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { mealCompositionService } from '@/services/mealComposition';
import { MEAL_COMPOSITIONS } from '@/constants/queryKeys';
import { PageLayout } from '@/components/layout';
import CompositionRecipeDisplay from '@/components/composition-detail/CompositionRecipeDisplay';
import CompositionHeaderActions from '@/components/composition-detail/CompositionHeaderActions';

const CompositionDetail = () => {
  const { id } = useParams<{ id: string }>();

  const {
    data: composition,
    isLoading
  } = useQuery({
    queryKey: [MEAL_COMPOSITIONS, id],
    queryFn: () => mealCompositionService.getMealCompositionById(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes - compositions change less frequently
  });

  // Content loading is now handled by simple ScrollToTop component

  if (isLoading) {
    return (
      <PageLayout 
        title="Loading..." 
        description="Loading composition details"
        maxWidth="max-w-4xl"
      >
        <div className="space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-60 w-full" />
        </div>
      </PageLayout>
    );
  }
  
  if (!composition) {
    return (
      <PageLayout 
        title="Composition Not Found" 
        description="The composition you're looking for doesn't exist or has been removed."
        maxWidth="max-w-4xl"
      >
        <div className="text-center py-12">
          {/* Content already handled by PageLayout title/description */}
        </div>
      </PageLayout>
    );
  }
  return (
    <PageLayout 
      title={composition.name}
      description={`A meal composition containing ${composition.recipes.length} recipe${composition.recipes.length !== 1 ? 's' : ''}`}
      headerRight={<CompositionHeaderActions composition={composition} />}
      maxWidth="max-w-4xl"
    >
      <CompositionRecipeDisplay recipes={composition.recipes} />
    </PageLayout>
  );
};
export default CompositionDetail;